import React from 'react';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import translate from '@/lib/intl/translate';
import ConstituentPropType from '@/lib/types/ConstituentPropType';
import { Dialogs, useDialogManager } from '@/modules/Dialogs';

export default function BulkEditActions({
  allConstituentsCount,
  allowMultiSelect,
  clearSelectedItems,
  constituents,
  excludedItems,
  query,
  selectedItems,
  setSelectedItems,
}) {
  const { closeDialog, openDialog } = useDialogManager();
  const [selectedCount, setSelectedCount] = React.useState(
    selectedItems.length,
  );
  const [allSelected, setAllSelected] = React.useState(false);

  React.useEffect(() => {
    setSelectedCount(selectedItems.length);
  }, [selectedItems]);

  const clickSelectAll = () => {
    setSelectedItems(constituents.map(({ constituentId }) => constituentId));
    setAllSelected(true);
  };

  const openBulkEditBoardsDialog = () => {
    openDialog(Dialogs.CONSTITUENT_BULK_EDIT_BOARDS, {
      allConstituentsCount,
      allSelected,
      allowMultiSelect,
      clearSelectedItems,
      onClose: closeDialog,
      excludedItems,
      query,
      selectedCount,
      selectedItems,
    });
  };
  const openBulkEditLabelsDialog = () => {
    openDialog(Dialogs.CONSTITUENT_BULK_EDIT_LABELS, {
      allConstituentsCount,
      allSelected,
      allowMultiSelect,
      clearSelectedItems,
      closeDialog,
      excludedItems,
      query,
      selectedCount,
      selectedItems,
    });
  };

  const count = React.useMemo(() => {
    if (allSelected) {
      return allConstituentsCount - excludedItems.length;
    }
    return selectedCount;
  }, [excludedItems, selectedCount]);

  return (
    <div className="flex flex-col items-center justify-between rounded-lg border border-grey-300 bg-grey-100 px-3 py-2 text-center text-sm font-semibold md:flex-row md:text-left">
      <div className="mr-0.5 flex items-center md:mr-0">
        <div>
          {translate('CONSTITUENT_LIST_SELECTED', {
            count,
            allSelected,
          })}
        </div>
        {!allSelected && !allowMultiSelect && (
          <div>
            <span className="px-2">|</span>
            <Button
              size="small"
              className="pr-0 pl-0 text-sm md:pr-4"
              onClick={clickSelectAll}
            >
              {translate('CONSTITUENT_LIST_SELECT_ALL', {
                count: allConstituentsCount,
              })}
            </Button>
          </div>
        )}
      </div>
      <div className="mr-0.5 md:mr-0">
        <Button
          size="small"
          className="mr-4 pl-0 md:pl-4"
          onClick={clearSelectedItems}
        >
          {translate('CONSTITUENT_LIST_CLEAR_SELECTED')}
        </Button>
        <Button
          className="mr-2"
          variant="contained"
          size="small"
          onClick={openBulkEditBoardsDialog}
        >
          {translate('CONSTITUENT_LIST_SET_BOARD')}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={openBulkEditLabelsDialog}
        >
          {translate('CONSTITUENT_LIST_ADD_LABELS')}
        </Button>
      </div>
    </div>
  );
}

BulkEditActions.propTypes = {
  allConstituentsCount: PropTypes.number,
  allowMultiSelect: PropTypes.bool,
  constituents: PropTypes.arrayOf(ConstituentPropType),
  clearSelectedItems: PropTypes.func.isRequired,
  excludedItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  query: PropTypes.arrayOf(PropTypes.shape({})),
  selectedItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedItems: PropTypes.func.isRequired,
};

BulkEditActions.defaultProps = {
  allConstituentsCount: 0,
  allowMultiSelect: false,
  constituents: [],
  query: [],
};
